import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Paper, CircularProgress, Box, Alert, Button } from "@mui/material";
import {
  checkSignInLink,
  completeSignInWithEmailLink,
  checkUserProfile,
} from "../../firebase";

const LoginComplete: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<
    "loading" | "success" | "error" | "need_email"
  >("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [email, setEmail] = useState("");
  const [url, setUrl] = useState("");

  // Helper function to get email from URL parameters
  const getEmailFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("email");
  };

  useEffect(() => {
    async function initializeLogin() {
      // Store the current URL
      const currentUrl = window.location.href;
      setUrl(currentUrl);

      // Check if this is a sign-in link
      if (!checkSignInLink(currentUrl)) {
        setStatus("error");
        setErrorMessage("Invalid sign-in link. Please try signing in again.");
        return;
      }

      // Try to get email from different sources in order of preference:
      // 1. URL parameters (for mobile redirects)
      // 2. localStorage (for same-device flows)
      let emailForSignIn = getEmailFromUrl();

      if (!emailForSignIn) {
        emailForSignIn = window.localStorage.getItem("emailForSignIn");
      }

      if (!emailForSignIn) {
        // We need to ask the user for their email
        setStatus("need_email");
        return;
      }

      // We have the email, proceed with sign-in
      completeLoginWithEmail(emailForSignIn);
    }

    initializeLogin();
  }, [navigate]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleSubmitEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      completeLoginWithEmail(email);
    } else {
      setErrorMessage("Please enter your email address.");
    }
  };

  const completeLoginWithEmail = async (emailForSignIn: string) => {
    setStatus("loading");

    try {
      // Complete the sign-in process
      const result = await completeSignInWithEmailLink(emailForSignIn, url);
      setStatus("success");

      // Check if user has a profile
      const userId = result.user.uid;
      const hasProfile = await checkUserProfile(userId);

      // Redirect after a short delay
      setTimeout(() => {
        if (hasProfile) {
          navigate("/portal/dashboard");
        } else {
          navigate("/register");
        }
      }, 2000);
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(
        error.message || "Failed to complete sign in. Please try again."
      );
    }
  };

  return (
    <div className="p-6 w-full max-w-md mx-auto bg-white rounded-lg shadow-md mt-10">
      <Box className="flex flex-col items-center">
        {status === "loading" && (
          <>
            <CircularProgress size={60} className="mb-4" />
            <p>Completing sign in...</p>
          </>
        )}

        {status === "need_email" && (
          <>
            <p>Confirm Your Email</p>
            <p>
              Please enter the email address you used to request the sign-in
              link.
            </p>
            <form onSubmit={handleSubmitEmail} className="w-full">
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Your email address"
                className="w-full p-2 mb-4 border rounded"
                required
              />
              {errorMessage && (
                <Alert severity="error" className="mb-4">
                  {errorMessage}
                </Alert>
              )}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
              >
                Complete Sign In
              </Button>
            </form>
          </>
        )}

        {status === "success" && (
          <>
            <Alert severity="success" className="w-full mb-4">
              You have been successfully signed in!
            </Alert>
            <p>Redirecting...</p>
          </>
        )}

        {status === "error" && (
          <>
            <Alert severity="error" className="w-full mb-4">
              {errorMessage}
            </Alert>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/login")}
            >
              Back to Login
            </Button>
          </>
        )}
      </Box>
    </div>
  );
};

export default LoginComplete;
